import { useState, useMemo } from "react";
import PropTypes from "prop-types";
import { Avatar } from "@mantine/core";
import {
  HiCheckCircle,
  HiPencil,
  HiExclamationTriangle,
} from "react-icons/hi2";
import BaseTable from "../../../ui/CustomTanStackTable/CustomTanStackTable";
import CustomSearchBar from "../../../ui/CustomSearchBar/CustomSearchBar";
import CustomBadge from "../../../ui/CustomBadge/CustomBadge";
import FetchFeedback from "../../../ui/CustomFetchFeedback/CustomFetchFeedback";
import BatchTransactionsSummaryFooter from "../../BatchSummary/components/BatchTransactionsSummaryFooter";
import { useGetBatchValidationRecipients } from "../../../../hooks/queries/useBulkUploadsQueries";
import {
  formatKES,
  getDisplayValue,
  getInitials,
} from "../../../../utils/transactionutils";

const RECIPIENT_STATUS_MAP = {
  SUCCESS: "success",
  FAILED: "error",
  VALIDATION_FAILED: "error",
  PROCESSING: "primary",
  PENDING: "gray",
};

const DEFAULT_PAGINATION = { pageIndex: 0, pageSize: 10 };

function RecipientCell({ name }) {
  const initials = getInitials(name);
  if (!initials) {
    return (
      <span className="text-sm font-medium text-gray-800">
        {getDisplayValue(name)}
      </span>
    );
  }
  return (
    <div className="flex items-center gap-2">
      <Avatar color="primary" radius="xl" size="sm">
        {initials}
      </Avatar>
      <span
        className="text-sm font-medium text-gray-800 truncate max-w-[140px]"
        title={name}
      >
        {getDisplayValue(name)}
      </span>
    </div>
  );
}

RecipientCell.propTypes = { name: PropTypes.string };

function buildColumns({ isBank, paymentChannel, onEdit }) {
  const identifierColumns = isBank
    ? [
        {
          accessorKey: "accountNumber",
          header: "Account No.",
          cell: (info) => getDisplayValue(info.getValue()),
          enableSorting: false,
        },
        {
          accessorKey: "bankCode",
          header: "Bank Code",
          cell: (info) => getDisplayValue(info.getValue()),
          enableSorting: false,
        },
      ]
    : [
        {
          accessorKey: "phoneNumber",
          header: "Phone",
          cell: (info) => getDisplayValue(info.getValue()),
          enableSorting: false,
        },
      ];

  return [
    {
      accessorKey: "recipientName",
      header: "Recipient",
      cell: (info) => <RecipientCell name={info.getValue()} />,
      enableSorting: false,
      size: 200,
    },
    ...identifierColumns,
    {
      accessorKey: "amount",
      header: "Amount",
      cell: (info) => formatKES(info.getValue()),
      enableSorting: false,
    },
    {
      accessorKey: "remarks",
      header: "Reference",
      cell: (info) => getDisplayValue(info.getValue()),
      enableSorting: false,
    },
    {
      id: "channel",
      header: "Channel",
      cell: () => (
        <CustomBadge color="primary" variant="light" size="sm">
          {paymentChannel}
        </CustomBadge>
      ),
      enableSorting: false,
      size: 100,
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: (info) => {
        const status = info.getValue();
        return (
          <CustomBadge
            color={RECIPIENT_STATUS_MAP[status] ?? "gray"}
            variant="light"
            size="sm"
          >
            {getDisplayValue(status)}
          </CustomBadge>
        );
      },
      enableSorting: false,
      size: 110,
    },
    {
      id: "actions",
      header: "Actions",
      cell: (info) => {
        const row = info.row.original;
        const hasFailed = row.status === "VALIDATION_FAILED";

        if (hasFailed) {
          return (
            <div className="flex items-center gap-1.5">
              <button
                title="Edit recipient"
                className="p-1.5 rounded-md text-primary-600 hover:bg-primary-50 transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit?.(row);
                }}
              >
                <HiPencil size={16} />
              </button>
              <HiExclamationTriangle
                size={16}
                className="text-red-500 shrink-0"
              />
            </div>
          );
        }

        return (
          <div className="flex items-center gap-1.5 p-1.5">
            <HiCheckCircle
              size={18}
              className="text-green-500 shrink-0"
              title="No errors"
            />
          </div>
        );
      },
      enableSorting: false,
      size: 80,
    },
  ];
}

function ValidationRecipientsTable({ disbursementId, paymentChannel, onEdit }) {
  const isBank = paymentChannel === "Bank";

  const [searchQuery, setSearchQuery] = useState("");
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);

  const handleSearchChange = (q) => {
    setSearchQuery(q);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const { data, isLoading, isFetching, isError, refetch } =
    useGetBatchValidationRecipients(disbursementId, {
      searchQuery,
      page: pagination.pageIndex,
      size: pagination.pageSize,
    });

  const content = data?.content ?? [];

  const columns = useMemo(
    () => buildColumns({ isBank, paymentChannel, onEdit }),
    [isBank, paymentChannel, onEdit],
  );

  if (isError) {
    return (
      <FetchFeedback
        message="Failed to load recipients. Please try again."
        onRetry={refetch}
        fullHeight={false}
        compact
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <CustomSearchBar
        placeholder={
          isBank
            ? "Search by name or account number"
            : "Search by name or phone number"
        }
        onSearchChange={handleSearchChange}
        debounceDelay={600}
        size="sm"
        radius="md"
        style={{ width: 260 }}
      />
      <BaseTable
        data={content}
        columns={columns}
        paginationMode="server"
        pageSize={pagination.pageSize}
        totalPages={data?.totalPages ?? 0}
        totalElements={data?.totalElements ?? 0}
        pagination={pagination}
        isLoading={isLoading}
        isFetching={isFetching}
        onPaginationChange={setPagination}
        emptyMessage={
          searchQuery
            ? "No recipients match your search."
            : "No recipients found."
        }
        rowClassName={(row) =>
          row.original.status === "VALIDATION_FAILED"
            ? "bg-red-50 min-h-[50px]"
            : "bg-white min-h-[50px]"
        }
      />
      <BatchTransactionsSummaryFooter
        subtotal={data?.recipientsSummation?.amount}
        totalCharges={data?.recipientsSummation?.charges}
        grandTotal={data?.recipientsSummation?.totalAmount}
        isLoading={isLoading}
        isError={isError}
      />
    </div>
  );
}

ValidationRecipientsTable.propTypes = {
  disbursementId: PropTypes.string.isRequired,
  paymentChannel: PropTypes.string.isRequired,
  onEdit: PropTypes.func,
};

export default ValidationRecipientsTable;
